
































































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import {State, Action, Mutation} from 'vuex-class'
import MenuEntry from '@/modules/cms/modules/menu/model/MenuEntry.ts'
import {Route} from 'vue-router'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import MenuTreeNode from '@/modules/cms/modules/menu/components/MenuTreeNode.vue'
import {Watch} from 'vue-property-decorator'
import MenuItemDetail from '@/modules/cms/modules/menu/components/MenuItemDetail.vue'
import {notify, Colors} from '@/modules/core/notification'
import Slot from '@/modules/cms/modules/menu/model/Slot'
import LoadingScreen from '@/modules/core/LoadingScreen'
import {SaveEvent} from '@/modules/cms/modules/menu'
import 'he-tree-vue/dist/he-tree-vue.css'
import {Tree, Node, Fold, Draggable, Store} from 'he-tree-vue'
import MenuType from '@/modules/cms/modules/menu/model/MenuType'
import {VForm} from '@/types';
import Loader from "@/components/Loader.vue";

/**
 * Pantalla de edición de menús
 *
 * Permite a un usuario ver la información de un menú, nombre, posición y los
 * elementos que contiene.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {
        Loader,
        MenuTreeNode,
        MenuItemDetail,
        Tree: Tree.mixPlugins([Fold, Draggable]),
    },
})
export default class MenuDetail extends Vue {
    public $refs: {
        tree: Tree,
        settingsForm: VForm,
    }

    /**
     * Permite establecer el menú actualmente mostrado al usuario
     */
    @Mutation('cms/menu/current/set')
    public setCurrentMenu: (menu: MenuEntry) => void

    /**
     * Permite establecer el sub-elemento mostrado al usuario, es decir, uno de
     * los hijos del menú principal, mostrado en la pantalla derecha
     */
    @Mutation('cms/menu/current/item/set')
    public setCurrentMenuItem: (item: MenuEntry) => void

    /**
     * Mutación que permite establecer si el usuario esta creando un nuevo
     * elemento para el menú
     */
    @Mutation('cms/menu/current/creating-item')
    public setCreatingItem: (creating: boolean) => void

    /**
     * Menu actualmente seleccionado por el usuario
     */
    @State(state => state.cms.menu.currentMenu)
    public menu: MenuEntry

    /**
     * Sub-item del menu actualmente seleccionado
     */
    @State(state => state.cms.menu.currentMenuItem)
    public currentItem: MenuEntry

    /**
     * Indica si el usuario esta creando un nuevo elemento de menú
     */
    @State(state => state.cms.menu.creatingItem)
    public creatingItem: boolean

    @State(state => state.cms.menu.slots)
    public slots: Slot[]

    /**
     * Indica si el usuario ha seleccionado alguno de los elementos de menú disponibles
     * en la pantalla del árbol de elementos
     */
    @State(state => state.cms.menu.itemSelected)
    public itemSelected: boolean

    /**
     * Carga un menu desde la API
     */
    @Action('cms/menu/loadMenu')
    public load: (id: number) => boolean

    @Action('cms/menu/createNewMenu')
    public createNewMenu: () => void

    @Action('cms/menu/loadSlotList')
    public loadSlotList: () => void

    @Mutation('cms/menu/current-locale/set')
    public setCurrentLocale: (locale: string) => void

    @Mutation('cms/menu/new-item/insertion-point/set')
    public setNewItemInsertionPoint: (id: string) => void

    @Mutation('cms/menu/TOGGLE_ITEM_SELECTED')
    public toggleItemSelected: (selected: boolean) => void

    public formValid = true

    public validationRules = {
        name: [
            (v: string) => !!v || 'El nombre es obligatorio',
        ],
        position: [
            (v: string) => !!v || 'La posición del menú es obligatoria',
        ],
    }

    /**
     * Controla la visibilidad de la pantalla que muestra los ajustes generales
     * del menú.
     */
    public generalSettings = false

    public saveConfirm = false
    public saving = false
    public loading = true
    public draggable = true
    public folded = false

    /**
     * Al activar esta opción el observador encargando de convertir el menú
     * en el estado a menú virtual será desactivado.
     */
    public disableMenuWatcher = false

    /**
     * Árbol virtual
     *
     * Estructura de datos intermedia entre el menú almacenado en el estado de la aplicación
     * y el componente encargado de mostrar el árbol anidado
     */
    public virtualTree: Node[] = []

    @Watch('virtualTree')
    watchVirtualTree () {
        console.log('El árbol ha cambiado')
    }

    /**
     * Observa el menu almacenado en el estado, si este cambia, re-genera el
     * árbol virtual mostrado al usuario
     */
    @Watch('menu', {deep: true})
    public onMenuChange (menu: MenuEntry) {
        if (this.disableMenuWatcher) {
            return
        }

        this.virtualTree = this.convertToVirtualTree(menu.items)
    }

    /**
     * Expandir o contraer menú.
     *
     * Método ejecutado cuando el usuario pulsa sobre el botón de contraer/expandir
     * menú.
     */
    public toggleFoldMenu () {
        const toggle = !this.folded
        toggle ? this.$refs.tree.foldAll() : this.$refs.tree.unfoldAll()

        this.folded = toggle
    }

    /**
     * Handler para el evento DragEnd del árbol
     *
     * Este método es llamado cuando el usuario arrastra un elemento y lo suelta.
     * Comprueba que el item de destino sea de tipo "colección de elementos".
     * Si no es asi no le permitirá soltar el elemento y este volverá
     * a su posición original.
     */
    public onDragEnd (tree: Tree, store: Store) {
        const path = [...store.targetPath]
        // La ruta solo tiene un elemento, el usuario ha movido un item de menú
        // y lo ha colocado en el primer nivel del árbol. La operación es
        // válida
        if (path.length === 1) {
            return true
        }

        path.pop()
        const parentNode = tree.getNodeByPath(path)
        if (parentNode.type !== MenuType.Collection) {
            // Notifica al usuario que la acción es inválida
            notify(
                'Los elementos de menú solo pueden ser insertados en colecciones',
                Colors.Warning,
                { notShowInCenter: true },
            )
            return false
        }

        return true
    }

    /**
     * Guarda los cambios realizados al menú actual
     */
    public async save () {
        this.saving = true
        try {
            await this.$store.dispatch('cms/menu/saveCurrentMenu')
        } catch (error) {
            this.saving = false
            this.saveConfirm = false

            return
        }

        this.saving = false
        this.saveConfirm = false
    }

    /**
     * Indica si el árbol virtual esta vacío
     */
    public get treeIsEmpty (): boolean {
        return this.virtualTree.length < 1
    }

    /**
     * Convierte una entrada de menu en un nodo virtual
     */
    private convertToPlainNode (entry: MenuEntry): KeyValuePair {
        const node: KeyValuePair = {
            id: entry.id || 0,
            tempId: entry.tempId,
            name: entry.name || '',
            type: entry.type,
        }

        if (entry.isCollection) {
            node.children = entry.items.map(e => this.convertToPlainNode(e))
        }

        return node
    }

    /**
     * Convierte una lista de elementos de menú en una lista de nodos para el
     * árbol virtual mostrado al usuario
     */
    private convertToVirtualTree (items: MenuEntry[]): Node[] {
        return items.map(i => this.convertToPlainNode(i)) as Node[]
    }

    /**
     * Actualiza un campo del menu actualmente seleccionado
     */
    public updateMenuField (path: string, value: any) {
        this.$store.commit('cms/menu/UPDATE_MENU_FIELD', {path, value})
    }

    /**
     * Método invocado cuando virtualTree cambia, actualiza el menu en el store con
     * las modificaciones realizadas al árbol virtual
     */
    public updateMenu (tree: Node[]): void {
        // Los cambios del árbol durante la creación del elemento no se aplican
        if (this.creatingItem) {
            return
        }

        this.disableMenuWatcher = true
        this.virtualTree = tree

        // const menu = _.cloneDeep(this.menu)
        const menu = this.menu
        this.updateMenuEntry(menu, menu, this.virtualTree)
        this.setCurrentMenu(menu)
        this.$nextTick(() => this.disableMenuWatcher = false)
    }

    /**
     * Activa el modo de creación de un elemento
     */
    public createNewItem (parent: string, node?: Node): void {
        node && this.$refs.tree.unfold(node)
        this.setCreatingItem(true)
        this.toggleItemSelected(true)
        this.addPlaceholderToParent(parent)
        this.setNewItemInsertionPoint(parent)

        this.setCurrentMenuItem(new MenuEntry())

        // Deshabilita el arrastre de todos los elementos durante la creación de un
        // nuevo elemento
        this.setTreeDraggable(false)
    }

    /**
     * Cambia el estado de todos los nodos del árbol para impedir o permitir
     * que el usuario pueda arrastrar y mover los elementos
     */
    private setTreeDraggable (active: boolean) {
        this.draggable = active
    }

    /**
     * Añade un nodo para representa un placeholder de nuevo elemento
     */
    public addPlaceholderToParent (id: string) {
        // No existe ningún padre en el menú, se inserta como elemento en la raiz
        if (!id) {
            this.virtualTree.push(this.buildPlaceholderNode())

            return
        }

        const parent = this.findNode(this.virtualTree, id)
        if (!parent) {
            throw new Error(`No existe el nodo con id temporal ${id} en el arbol virtual`)
        }

        parent.children!.push(this.buildPlaceholderNode())
    }

    /**
     * Crea un nodo utilizado como placeholder para el nuevo elemento que se va
     * a insertar
     */
    private buildPlaceholderNode (): Node {
        return {
            tempId: 'new_node_placeholder',
            placeholderForNewItem: true,
        }
    }

    /**
     * Sale del modo de creación de un nuevo elemento
     */
    public exitCreatingMode (): void {
        // Elimina el nodo virtual correspondiente al placeholder
        this.removeNode(this.virtualTree, 'new_node_placeholder')
        this.setCreatingItem(false)

        // Vuelve a habilitar el arrastre de elementos
        this.setTreeDraggable(true)
    }

    /**
     * Busca un nodo en el arbol virtual en base al campo tempId
     */
    private findNode (nodes: Node[], id: string): Node | null {
        for (const node of nodes) {
            if (node.tempId === id) {
                return node
            }

            if (Array.isArray(node.children) && node.children.length) {
                const n = this.findNode(node.children, id)
                if (n) {
                    return n
                }
            }
        }

        return null
    }

    /**
     * Elimina un nodo del árbol virtual en base al campo tempid
     */
    private removeNode (nodes: Node[], id: string): Node[] {
        for (const index in nodes) {
            if (!nodes[index]) {
                continue
            }

            const node = nodes[index]
            if (node.tempId === id) {
                nodes.splice(parseInt(index, 10), 1)

                return nodes
            }

            if (Array.isArray(node.children) && node.children.length) {
                this.removeNode(node.children, id)
            }
        }

        return nodes
    }

    /**
     * Aplica una función a todos los nodos de un árbol
     */
    public applyToNodes (nodes: Node[], fn: (node: Node) => any) {
        for (const node of nodes) {
            fn(node)

            if (Array.isArray(node.children) && node.children.length) {
                this.applyToNodes(node.children, fn)
            }
        }
    }

    /**
     * Actualiza una entrada de menu con los nodos del árbol virtual
     */
    private updateMenuEntry (root: MenuEntry, entry: MenuEntry, nodes: Node[]) {
        nodes.forEach(node => {
            const child = root.findItemByTempId(node.tempId, true)
            if (!child) {
                throw new Error(`No existe el elemento con id ${node.tempId}`)
            }

            root.deleteItem(node.tempId)

            // Añade la entrada a la lista de hijos actual
            entry.items.push(child)

            if (Array.isArray(node.children) && node.children.length) {
                this.updateMenuEntry(root, child, node.children)
            }
        })
    }

    /**
     * Método invocado cada vez que el usuario guarda los cambios de un elemento de menú
     */
    public onItemSave (event: SaveEvent) {
        this.exitCreatingMode()

        this.virtualTree = this.convertToVirtualTree(this.menu.items)
    }

    @Watch('$route')
    public async onRoute (route: Route) {
        await this.loadFromRoute(route)
    }

    /**
     * Carga un menu a partir de los parámetros de la ruta
     */
    private async loadFromRoute (route: Route) {
        this.loading = true
        LoadingScreen.show('Cargando menú')

        await this.loadSlotList()

        const id = route.params['id'] || ''

        // El id no esta presenta en la información de la ruta, no se puede carga el menú
        if (!id) {
            await this.$router.push({name: 'dashboard'})
            notify('Es necesario el id del menú', Colors.Warning)
        } else if ('nuevo' === id) {
            // El id es la palabra 'nuevo' se inserta un menú limpio en el estado de la
            // aplicación para que el usuario procesa a crearlo
            await this.createNewMenu()
            this.generalSettings = true
        } else {
            const success = await this.load(parseInt(id, 10))
            if (!success) {
                await this.$router.push({name: 'dashboard'})
            }
        }

        LoadingScreen.hide()
        this.loading = false
    }

    /**
     * Carga el menú según el id portado en la ruta y luego inicializa el árbol
     * virtual
     */
    public async created () {
        // El idioma por defecto es el español
        this.setCurrentLocale('es')

        await this.loadFromRoute(this.$route)
        this.virtualTree = this.menu.items.map(i => this.convertToPlainNode(i)) as Node[]
    }
}
